/**
 * GameShell chrome: header bar (identity, cash, clock, speed controls),
 * navigation (bottom tabs on mobile / side rail on desktop), and the global
 * modals (pending risk event, away summary, game over).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banknote,
  Boxes,
  FlaskConical,
  LayoutDashboard,
  Megaphone,
  Newspaper,
  Package,
  Pause,
  Play,
  Store,
  FastForward,
  Home,
  Download,
  Trophy,
  Volume2,
  VolumeX,
  X,
  Scale,
  Flame,
  Swords,
  Bot,
  Zap,
  Truck,
  ScrollText,
  Gift,
  PartyPopper,
  Landmark,
  Award,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useGameStore, type Speed } from '@/store/gameStore';
import { formatGameDate, money, quarterLabel } from '@/game/format';
import { EVENT_BY_ID } from '@/game/events';
import { EVENT_CATEGORY_META } from '@/game/constants';
import { exportSaveFile } from '@/game/save';
import { sfx } from '@/game/audio';
import { buildPayload, recordLocalRun, submitRun } from '@/game/leaderboard';
import { BankruptcyScene, BeachExitScene } from '@/components/game/art/Scenes';
import type { EventCategory } from '@/game/types';

/** Category → icon so every interruption reads at a glance. */
const EVENT_ICONS = {
  trade: Scale,
  macro: Flame,
  competition: Swords,
  platform: Bot,
  viral: Zap,
  supply: Truck,
  regulatory: ScrollText,
  opportunity: Gift,
  positive: PartyPopper,
  finance: Landmark,
  milestone: Award,
} as const satisfies Record<EventCategory | 'finance' | 'milestone', typeof Scale>;

export type TabId = 'dashboard' | 'products' | 'supply' | 'channels' | 'marketing' | 'finance' | 'research' | 'news' | 'ranks';

export const TABS: { id: TabId; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'supply', label: 'Supply', icon: Boxes },
  { id: 'channels', label: 'Channels', icon: Store },
  { id: 'marketing', label: 'Ads', icon: Megaphone },
  { id: 'finance', label: 'Finance', icon: Banknote },
  { id: 'research', label: 'Research', icon: FlaskConical },
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'ranks', label: 'Ranks', icon: Trophy },
];

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const SPEEDS: Speed[] = [0, 1, 2, 3];

export function HeaderBar() {
  const state = useGameStore((s) => s.state);
  const speed = useGameStore((s) => s.speed);
  const setSpeed = useGameStore((s) => s.setSpeed);
  const soundOn = useGameStore((s) => s.soundOn);
  const setSound = useGameStore((s) => s.setSound);
  if (!state) return null;

  const cash = state.player.cash;
  const lastNet = state.history.daily.length > 0 ? state.history.daily[state.history.daily.length - 1].net : 0;
  const q = quarterLabel(state.time.day);

  return (
    <header className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-3 py-2 sm:gap-4 sm:px-4">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-tight">{state.player.companyName}</p>
          <p className="text-[11px] leading-tight text-muted-foreground tabular-nums">
            {formatGameDate(state.time.day)} · {q.label}
          </p>
        </div>

        <div className="hidden items-center gap-1 rounded-full border bg-card px-2.5 py-1 text-xs md:flex" title="WTI crude spot — drives freight surcharges (lagged) and foam feedstock">
          <span className="font-medium tabular-nums">${state.world.oil.toFixed(0)}</span>
          <span className="text-muted-foreground">WTI</span>
        </div>

        <div className="text-right">
          <p className={cn('text-sm font-bold leading-tight tabular-nums sm:text-base', cash < 0 ? 'text-red-600 dark:text-red-400' : '')}>{money(cash)}</p>
          <p className={cn('text-[11px] leading-tight tabular-nums', lastNet >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
            {lastNet >= 0 ? '+' : ''}
            {money(lastNet)} today
          </p>
        </div>

        <button
          onClick={() => setSound(!soundOn)}
          aria-label={soundOn ? 'Mute sound effects' : 'Unmute sound effects'}
          className="rounded-lg border bg-card p-2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>

        <div className="flex items-center rounded-lg border bg-card p-0.5" role="group" aria-label="Simulation speed">
          {SPEEDS.map((sp) => (
            <button
              key={sp}
              onClick={() => setSpeed(sp)}
              aria-label={sp === 0 ? 'Pause' : `Speed ${sp}`}
              aria-pressed={speed === sp}
              className={cn(
                'rounded-md px-2 py-1.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                speed === sp && 'bg-primary text-primary-foreground hover:text-primary-foreground',
              )}
            >
              {sp === 0 ? <Pause className="h-4 w-4" /> : sp === 1 ? <Play className="h-4 w-4" /> : <FastForward className={cn('h-4 w-4', sp === 3 && 'translate-x-0.5')} />}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

export function SideNav({ active, onSelect }: { active: TabId; onSelect: (t: TabId) => void }) {
  const state = useGameStore((s) => s.state);
  const navigate = useNavigate();
  if (!state) return null;
  const pendingCount = state.pos.length;
  const unreadNews = Math.min(9, state.news.filter((n) => n.day > state.time.day - 7).length);

  return (
    <nav className="flex h-full flex-col gap-1 p-3" aria-label="Game sections">
      <div className="mb-2 flex items-center justify-between">
        <span className="px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Empire</span>
        <Button variant="ghost" size="icon" onClick={() => navigate('/')} aria-label="Title screen">
          <Home className="h-4 w-4" />
        </Button>
      </div>
      {TABS.map((t) => (
        <button
          key={t.id}
          onClick={() => onSelect(t.id)}
          aria-current={active === t.id ? 'page' : undefined}
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            active === t.id && 'bg-accent text-accent-foreground',
          )}
        >
          <t.icon className="h-4 w-4 shrink-0" />
          {t.label}
          {t.id === 'news' && unreadNews > 0 && (
            <Badge variant="secondary" className="ml-auto h-5 min-w-5 px-1 text-[10px]">{unreadNews}</Badge>
          )}
          {t.id === 'supply' && pendingCount > 0 && (
            <Badge variant="outline" className="ml-auto h-5 min-w-5 px-1 text-[10px]">{pendingCount}</Badge>
          )}
        </button>
      ))}
      <Separator className="my-2" />
      <button
        onClick={() => exportSaveFile(state)}
        className="mt-auto flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        <Download className="h-4 w-4 shrink-0" /> Export save
      </button>
    </nav>
  );
}

export function BottomNav({ active, onSelect }: { active: TabId; onSelect: (t: TabId) => void }) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 border-t bg-background/95 backdrop-blur lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Game sections"
    >
      <div className="grid grid-cols-9">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => onSelect(t.id)}
            aria-current={active === t.id ? 'page' : undefined}
            className={cn(
              'flex min-h-14 flex-col items-center justify-center gap-0.5 py-1 text-[10px] text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
              active === t.id && 'text-primary',
            )}
          >
            <t.icon className="h-5 w-5" />
            <span className="leading-none">{t.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Pending event modal
// ---------------------------------------------------------------------------

export function EventModal() {
  const state = useGameStore((s) => s.state);
  const resolveEvent = useGameStore((s) => s.resolveEvent);
  const def = state?.pendingEventId ? EVENT_BY_ID[state.pendingEventId] : null;
  const eventId = def?.id;

  // Cue a sound when a new event interrupts the run.
  useEffect(() => {
    if (!eventId) return;
    const activeDef = EVENT_BY_ID[eventId];
    sfx.play(activeDef && activeDef.severity >= 3 ? 'bad' : 'event');
  }, [eventId]);

  const body = useMemo(() => def?.body ?? '', [def]);
  if (!state || !def) return null;

  const meta = EVENT_CATEGORY_META[def.category];
  const severityColor =
    def.severity >= 4 ? 'border-red-500/60' : def.severity === 3 ? 'border-amber-500/60' : 'border-border';

  return (
    <Dialog open onOpenChange={() => {/* forced: must choose or acknowledge */}}>
      <DialogContent className={cn('max-w-md border-2', severityColor)} showCloseButton={false}>
        <DialogHeader>
          <div className="flex items-center gap-2 pb-1">
            <Badge variant="outline" className="gap-1.5">
              {(() => {
                const Icon = EVENT_ICONS[def.category];
                return <Icon className="h-3.5 w-3.5" />;
              })()}
              {meta.label}
            </Badge>
            {def.severity >= 3 && <Badge variant="destructive">Severity {def.severity}/5</Badge>}
          </div>
          <DialogTitle className="text-left text-lg leading-snug">{def.title}</DialogTitle>
          <DialogDescription className="text-left text-sm leading-relaxed">{body}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(def.choices ?? []).map((c) => {
            const cost = typeof c.cost === 'function' ? c.cost(state) : c.cost;
            const affordable = !cost || state.player.cash >= cost;
            return (
              <button
                key={c.id}
                disabled={!affordable}
                onClick={() => resolveEvent(c.id)}
                className={cn(
                  'w-full rounded-xl border bg-card p-3 text-left transition-colors hover:border-primary/60 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40',
                )}
              >
                <p className="text-sm font-medium">{c.label}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{c.detail}</p>
                {!affordable && <p className="mt-1 text-xs text-red-500">Not enough cash ({money(cost!)})</p>}
              </button>
            );
          })}
          {(!def.choices || def.choices.length === 0) && (
            <Button className="w-full" onClick={() => resolveEvent(null)}>Understood</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Away summary + Game over modals
// ---------------------------------------------------------------------------

export function AwayModal() {
  const summary = useGameStore((s) => s.awaySummary);
  const dismiss = useGameStore((s) => s.dismissAwaySummary);
  if (!summary) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>While you were away…</DialogTitle>
          <DialogDescription>
            {summary.daysSimulated} days simulated on autopilot.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1 text-sm">
          <li className="flex justify-between"><span className="text-muted-foreground">Cash change</span><span className={cn('tabular-nums font-medium', summary.cashDelta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>{money(summary.cashDelta, { sign: true })}</span></li>
          <li className="flex justify-between"><span className="text-muted-foreground">Units shipped</span><span className="tabular-nums font-medium">{summary.unitsSold.toLocaleString()}</span></li>
        </ul>
        <ul className="space-y-1 rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
          {summary.highlights.map((h, i) => <li key={i}>• {h}</li>)}
        </ul>
        <DialogFooter>
          <Button onClick={dismiss}>Back to work</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function GameOverModal() {
  const state = useGameStore((s) => s.state);
  const abandon = useGameStore((s) => s.abandonGame);
  const navigate = useNavigate();
  const over = state?.gameOver;

  const [submitState, setSubmitState] = useState<'sending' | 'ok' | 'fail' | null>(null);
  const [rank, setRank] = useState<number | null>(null);
  const submittedKey = useRef<string | null>(null);

  useEffect(() => {
    if (over) {
      // Comedy scoring: sad trombone for the bust, cha-ching fanfare for the exit.
      sfx.play(over.reason === 'acquired' ? 'cashBig' : 'trombone');
    }
  }, [over]);

  // Auto-submit the finished run to the global leaderboard once.
  useEffect(() => {
    if (!over || !state) return;
    const key = `${state.meta.seed}:${over.day}`;
    if (submittedKey.current === key) return;
    submittedKey.current = key;
    setSubmitState('sending');
    void (async () => {
      const payload = buildPayload(state, over.reason, over.valuation);
      const res = await submitRun(payload);
      recordLocalRun(payload, res.rank);
      setRank(res.rank);
      setSubmitState(res.ok ? 'ok' : 'fail');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over]);

  const retrySubmit = () => {
    if (!state || !over) return;
    setSubmitState('sending');
    void (async () => {
      const payload = buildPayload(state, over.reason);
      const res = await submitRun(payload);
      recordLocalRun(payload, res.rank);
      setRank(res.rank);
      setSubmitState(res.ok ? 'ok' : 'fail');
    })();
  };

  if (!over) return null;
  const go = over;
  return (
    <Dialog open>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" showCloseButton={false}>
        {go.reason === 'acquired' ? (
          <BeachExitScene className="w-full rounded-xl border border-sky-500/30" />
        ) : (
          <BankruptcyScene className="w-full rounded-xl border border-red-500/30" />
        )}
        <DialogHeader>
          <DialogTitle className={go.reason === 'acquired' ? '' : 'text-red-600 dark:text-red-400'}>{go.headline}</DialogTitle>
          <DialogDescription>
            {go.reason === 'acquired'
              ? 'You signed the term sheet. The brand lives on under new owners — you live on beach time.'
              : 'The runway hit zero. Credit exhausted, payroll bounced. A post-mortem for the ages.'}
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 rounded-lg border bg-muted/40 p-4 text-sm">
          {go.summary.map((l, i) => (
            <li key={i} className="flex justify-between gap-4"><span>{l.split(':')[0]}</span><span className="font-medium tabular-nums">{l.split(':').slice(1).join(':').trim()}</span></li>
          ))}
        </ul>
        <div className="flex min-h-6 items-center justify-center text-xs" aria-live="polite">
          {submitState === 'sending' && <span className="text-muted-foreground">Submitting to global ranks…</span>}
          {submitState === 'ok' && (
            <span className="font-medium text-emerald-600 dark:text-emerald-400">
              {rank != null ? `Recorded — Global Rank #${rank}` : 'Recorded (outside top 200)'}
            </span>
          )}
          {submitState === 'fail' && (
            <span className="text-amber-600 dark:text-amber-400">
              Couldn't reach the rankings API.{' '}
              <button onClick={retrySubmit} className="underline underline-offset-2 hover:text-foreground">
                Retry
              </button>
            </span>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { exportSaveFile(state); }}>
            <Download className="mr-2 h-4 w-4" /> Save post-mortem
          </Button>
          <Button onClick={() => { abandon(); navigate('/'); }}>
            <X className="mr-2 h-4 w-4" /> Start over
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
